import React, { useEffect, useRef, useState } from 'react';
import { Drawer, Form, Input, message, Space, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import CancelButton from '../common/cancelbutton';
import FormButton from '../common/formbutton';
import { useAuth } from '../../auth/authcontext';
import { checkPageVersionNameExists, duplicatePageVersion } from '../../services/pageversionservice';

const { Text } = Typography;

interface PageVersionDuplicateDrawerProps {
    open: boolean;
    sourcePageVersionId: string;
    sourcePageVersionName: string;
    onClose: () => void;
}

const PageVersionDuplicateDrawer: React.FC<PageVersionDuplicateDrawerProps> = ({
    open,
    sourcePageVersionId,
    sourcePageVersionName,
    onClose,
}) => {
    const [form] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);
    const [checkingName, setCheckingName] = useState(false);
    const [nameStatus, setNameStatus] = useState<string | null>(null);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const navigate = useNavigate();
    const { userId } = useAuth();
    const createdByGuid = userId || '';

    useEffect(() => {
        if (open) {
            form.resetFields();
            setNameStatus(null);
            setSubmitError(null);

            const initialName = `${sourcePageVersionName} - Copy`;

            // Set initial value and trigger validation
            form.setFieldsValue({ name: initialName });

            setTimeout(() => {
                inputRef.current?.focus();
                form.validateFields(['name']); // Trigger name uniqueness check
            }, 100);
        }
    }, [open, form, sourcePageVersionName]);


    const checkNameUnique = async (_: any, value: string) => {
        if (!value) {
            setNameStatus(null);
            return Promise.reject('Name is required');
        }
        setCheckingName(true);
        setNameStatus('Checking name...');
        try {
            const data = await checkPageVersionNameExists(value);
            if (data.exists) {
                setNameStatus('Name already exists');
                return Promise.reject('Name already exists');
            } else {
                setNameStatus('Name is unique');
                return Promise.resolve();
            }
        } catch {
            setNameStatus('Error checking name');
            return Promise.reject('Error checking name');
        } finally {
            setCheckingName(false);
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    };

    const handleSubmit = async () => {
        if (submitting) return; // Prevent duplicate clicks

        const hasErrors = form.getFieldsError().some(field => field.errors.length > 0);
        if (hasErrors) {
            setSubmitError('Please fix the errors before submitting');
            return;
        }

        try {
            const values = await form.validateFields();
            setSubmitting(true);
            const data = await duplicatePageVersion({
                sourcePageVersionGuid: sourcePageVersionId,
                name: values.name,
                createdBy: createdByGuid
              });
              
            message.success('PageVersion duplicated successfully');
            const newId = data.id;
            onClose();
            navigate(`/kioskpageversion/${newId}`);
        } catch (err: any) {
            setSubmitError(err?.response?.data?.message || 'Failed to duplicate PageVersion');
        } finally {
            setSubmitting(false);
        }
    };

    const currentName = Form.useWatch('name', form);

    const getValidateStatus = (
        name: string | undefined,
        checking: boolean,
        status: string | null
    ): 'success' | 'error' | 'validating' | undefined => {
        if (!name) return 'error';
        if (checking) return 'validating';
        if (status === 'Name is unique') return 'success';
        if (status === 'Name already exists' || status === 'Error checking name') return 'error';
        return undefined;
    };

    const getHelpMessage = (
        name: string | undefined,
        status: string | null
    ): React.ReactNode | undefined => {
        if (!name) {
            return <span style={{ color: 'red' }}>Name is required</span>;
        }
        if (status === 'Name already exists' || status === 'Error checking name') {
            return <span style={{ color: 'red' }}>{status}</span>;
        }
        if (status) {
            const color = status === 'Name is unique' ? '#52c41a' : 'rgba(0,0,0,0.45)';
            return <span style={{ color }}>{status}</span>;
        }
        return undefined;
    };

    return (
        <Drawer
            title="Duplicate PageVersion"
            placement="right"
            width={400}
            onClose={onClose}
            open={open}
            destroyOnClose
            maskClosable={!submitting}
            closable={false}
            styles={{
                header: {
                    color: 'white',
                    backgroundColor: 'var(--color-primary)',
                    fontWeight: 350,
                    fontSize: '14px',
                    border: 0,
                    minHeight: '40px',
                },
            }}
            footer={
                <Space style={{ display: 'flex', justifyContent: 'end' }}>
                    <CancelButton onClick={onClose} disabled={submitting} />
                    <FormButton title="Duplicate" onClick={handleSubmit} loading={submitting} />
                </Space>
            }
        >
            <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                Duplicating: <strong>{sourcePageVersionName}</strong>
            </Text>

            {submitError && (
                <div style={{
                    backgroundColor: '#fff1f0',
                    border: '1px solid #ffa39e',
                    padding: '8px 12px',
                    marginTop: '16px',
                    marginBottom: '16px',
                    borderRadius: '4px',
                    color: '#cf1322',
                }}>
                    {submitError}
                </div>
            )}

            <Form
                form={form}
                layout="vertical"
                preserve={false}
                initialValues={{ name: `${sourcePageVersionName} - Copy` }}
                onValuesChange={(changedValues) => {
                    if ('name' in changedValues) {
                        setSubmitError(null);
                        if (!changedValues.name) {
                            setNameStatus(null);
                        }
                    }
                }}
            >
                <Form.Item
                    label="New PageVersion Name"
                    name="name"
                    validateStatus={getValidateStatus(currentName, checkingName, nameStatus)}
                    hasFeedback
                    help={getHelpMessage(currentName, nameStatus)}
                    rules={[{ validator: checkNameUnique }]}
                >
                    <Input
                        placeholder="Enter unique name"
                        disabled={checkingName}
                        allowClear
                        ref={el => {
                            if (el) {
                                inputRef.current = el.input;
                            }
                        }}
                    />
                </Form.Item>
            </Form>
        </Drawer>
    );
};

export default PageVersionDuplicateDrawer;